/** Sens du changement de statut demandé sur les demandes cochées. */
export type StatusTransition = 'send' | 'sendBack';

export type AssignTechnicianPayload = {
  pds_ids: number[];
  /** L'API attend bien `technician`, et non `technician_id`. */
  technician: number;
};

/** Corps commun à la désaffectation et aux deux transitions de statut. */
export type PdsIdsPayload = { pds_ids: number[] };

/** Une demande refusée, avec de quoi l'identifier dans la liste. */
export type TransitionFailure = {
  pds_id: number;
  service_point_customer_id: string | null;
  equipment_id: string | null;
  device_id: string | null;
  error: string;
};

/**
 * Réponse des deux transitions de statut.
 *
 * `errors` est optionnel : la v1 l'accédait sans garde et plantait sur une réponse
 * muette. Le nombre d'échecs se dérive de `failed_ids`, qui est toujours présent.
 */
export type TransitionResult = {
  total_submitted: number;
  success_count: number;
  failed_ids: number[];
  errors?: TransitionFailure[];
};

/**
 * Réponse de l'affectation : même enveloppe de lot que les transitions de statut.
 *
 * Le back refuse une demande dont le contrat n'est pas dans le périmètre du technicien
 * (APPSUPPORT-2130). Le refus ne fait pas échouer l'appel : HTTP 200, la demande dans
 * `failed_ids` et le motif dans `errors`.
 */
export type AssignTechnicianResult = TransitionResult;

/** Bilan affichable d'un lot, commun à l'affectation et aux transitions de statut. */
export type BatchReport = {
  succeededCount: number;
  failedCount: number;
  failures: TransitionFailure[];
};

const ASSIGN_TECHNICIAN_BASE = '/api/boi/exocet/pds/assign-technician/';

function withCustomer(path: string, clientId: number): string {
  return `${path}?${new URLSearchParams({ customer_id: String(clientId) })}`;
}

/** Même URL que le GET de la liste : seul le verbe distingue les deux. */
export function buildAssignTechnicianUrl(clientId: number): string {
  return withCustomer(ASSIGN_TECHNICIAN_BASE, clientId);
}

export function buildAssignTechnicianPayload(
  technicianId: number,
  selectedRequestIds: number[]
): AssignTechnicianPayload {
  return { pds_ids: selectedRequestIds, technician: technicianId };
}

/**
 * Retire le technicien sans faire sortir la demande de cette étape.
 *
 * La faute de frappe « assignement » est celle du chemin réel côté API : la corriger
 * casserait l'appel.
 */
export function buildRemoveAssignmentUrl(clientId: number): string {
  return withCustomer(`${ASSIGN_TECHNICIAN_BASE}remove-assignement/`, clientId);
}

/**
 * `send` crée les interventions et fait sortir les demandes de cette étape ;
 * `sendBack` les renvoie à l'étape Qualification. Même corps, même forme de réponse.
 */
export function buildTransitionUrl(transition: StatusTransition, clientId: number): string {
  const path = transition === 'send' ? 'upgrade-status/' : 'downgrade-status/';

  return withCustomer(`${ASSIGN_TECHNICIAN_BASE}${path}`, clientId);
}
